import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import dayjs from 'dayjs';
import { ThemeProvider } from 'styled-components';
import { DeviceProvider, Portal } from '@codex/gadgets';
import { BoxSizing } from '@codex/basics';
import 'isomorphic-fetch';
import urljoin from 'url-join';
import themes from '@codex/themes';
import { formatPrice } from '@codex/helpers';
import translations, { replacePlaceholders } from '@dc/ui-library/src/config/translations'; // Temporary solution, translations will be moved to helpers.
import { Bundle } from '@codex-applications/bundles';
import withDataLayer, { dispatchSetProductBundlesList } from './withDataLayer';
import AddToBasketButtonWithAjaxCall
  from '../AddToBasket/AddToBasketButtonWithAjaxCall.jsx';
import { PSA_SWITCHED_OFF }
  from '../PopupStyleAttach/components/PSAPopup/PSAPopup';

const BUNDLES_API_FALLBACK = 'https://api.currys.co.uk/store/api';
const BUNDLES_API_PATH = '/products/[PRODUCT_ID]/visitors/[VISITOR_ID]/bundles';
const SELECTOR_BUNDLES_OLD = '.bundle-purchase';
const SELECTOR_BUNDLES_USER_CREATED = '.packBuilderSummary .productPurchase';
const SELECTOR_BUNDLES_FIRST = '[data-component="BundleFirst"]';
const SELECTOR_BUNDLES_LIST = '[data-component="BundleList"]';
const SELECTOR_BUNDLES_TAB_CONTENT = '.tab-bundles';
const SELECTOR_BUNDLES_TABS = '[href="#tab4"]';
const SELECTOR_BUNDLES_BUILDER = '#packBuilder';
const SELECTOR_PRODUCT_ID_STEP_PAGE = 'input[name="sFUPID"]';
const RECOMMENDATION_TYPES = {
  MANUAL: 'manual',
  BASKET: 'basket',
  PERSONALISED: 'personalised'
};

export class BundlesController extends Component {

  static getTranslations() {

    return {
      addToBasket: translations['bundle.add_to_basket'],
      created: translations['bundle.created'],
      mainProductHint: translations['bundle.main_product_hint'],
      save: translations['bundle.save_prefix'],
      total: translations['bundle.total'],
      totalSavings: translations['bundle.total_savings']
    };
  }

  static formatDiscountLabel(percentage) {
    const value = Math.trunc(percentage / 5) * 5; // 0-4 is 0, 5-9 is 5, 10-15 is 10, etc.
    const prefix = percentage !== value ? translations['bundle.discount_prefix'] : '';
    const suffix = translations['bundle.discount_suffix'];

    if (value === 0) {
      return null;
    }

    return `${prefix} ${value}% ${suffix}`;
  }

  constructor(props) {
    super(props);

    this.state = {
      bundles: [],
      theme: global.THEME || 'currys',
      translations: {}
    };
  }

  componentDidMount() {
    if (this.props.isProductBundle) {
      this.hideEmptyTabs();
    } else {
      this.fetchBundles();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.triggerElFilled();
  }

  onBundlesDisplayed(bundles) {
    const { props } = this;

    props.onBundlesReceived(bundles, props.oldBundlesCount, props.userCreatedBundlesCount);
    return bundles;
  }

  triggerElFilled() {
    const event = new CustomEvent('bundlesFilled', { bubbles: true });
    const el = document.querySelector('[data-component="BundleFirst"]');
    el.dispatchEvent(event);
  }

  hideEmptyTabs() {
    if (this.props.tabsHideable) {
      this.props.bundlesTabsRoots.forEach((tab) => tab.parentNode.removeChild(tab));

      if (this.props.bundlesTabContent) {
        this.props.bundlesTabContent.parentNode.removeChild(this.props.bundlesTabContent);
      }
    }
  }

  fetchBundles() {
    function handleError(response) {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    }

    if (this.props.automatedBundlesAllowed) {
      fetch(
        urljoin(this.props.storeApiUrl, '/token'),
        { method: 'POST', credentials: 'include' }
      )
        .then(handleError)
        .then((_) => fetch(
          urljoin(
            this.props.storeApiUrl,
            this.props.bundlesApiPath
          ),
          { credentials: 'include' }
        ))
        .then(handleError)
        .then((response) => {
          if (!response.payload || response.payload.length === 0) {
            throw Error('No bundles data fetched.');
          }
          return this.transformBundles(response.payload);
        })
        .then((bundles) => {
          if (!bundles || bundles.length < 2) {
            this.hideEmptyTabs();
          }
          return bundles;
        })
        .then((bundles) => this.onBundlesDisplayed(bundles))
        .then((bundles) => this.setState({
          bundles,
          translations: BundlesController.getTranslations()
        }))
        .catch((error) => {
          this.hideEmptyTabs();
          console.error(error);
        });
    }
  }

  transformBundles(bundles) {
    return bundles.map((bundle) => {
      const createdDate = dayjs(bundle.creationDate.replace('+0000', 'Z'));
      const totalSavings = bundle.price && bundle.price.discountAmount
        ? formatPrice(bundle.price.discountAmount, true, bundle.price.currencyCode)
        : null;

      return {
        id: bundle.recommendationType === RECOMMENDATION_TYPES.MANUAL
          ? bundle.id
          : `DS${bundle.data.mainProduct.id}`,
        bundleType: bundle.bundleType,
        recommendationType: bundle.recommendationType,
        createdDate: createdDate.isValid() ? createdDate.format('D MMM YYYY, HH:mm') : '',
        totalPrice: formatPrice(bundle.price.amount, true, bundle.price.currencyCode),
        totalSavings: totalSavings,
        heading: totalSavings
          ? replacePlaceholders(
            translations['bundle.heading_with_saving_price'], { '[PRICE]': totalSavings }
          )
          : translations['bundle.heading'],
        discountLabel: BundlesController.formatDiscountLabel(bundle.price.discountPercentage),
        priceDescription: bundle.data.mainProduct.wasPrice &&
          bundle.data.mainProduct.wasPrice.amount > 0
          ? replacePlaceholders(
            translations['bundle.price_description'],
            {
              '[WAS_PRICE]': formatPrice(
                bundle.data.mainProduct.wasPrice.amount,
                true,
                bundle.price.currencyCode
              ),
              '[PRICE]': bundle.data.mainProduct.price &&
                bundle.data.mainProduct.price.amount > 0
                ? formatPrice(
                  bundle.data.mainProduct.price.amount,
                  true,
                  bundle.price.currencyCode
                ) : NaN,
              '[SEPARATE_PRICE]': bundle.price &&
                bundle.price.separateAmount > 0
                ? formatPrice(
                  bundle.price.separateAmount,
                  true,
                  bundle.price.currencyCode
                ) : NaN
            }
          )
          : '',
        product: {
          ...bundle.data.mainProduct,
          name: `${bundle.data.mainProduct.brandLabel} ${bundle.data.mainProduct.name}`
        },
        addons: bundle.data.subProducts.map((addon) => ({
          ...addon,
          name: `${addon.brandLabel} ${addon.name}`
        }))
      };
    });
  }

  renderBundles(bundlesList) {
    return (
      <BoxSizing>
        {bundlesList.map((bundle, index) => (
          <Bundle
            key={index}
            id={bundle.id}
            addons={bundle.addons}
            bundleType={bundle.bundleType}
            dateCreated={bundle.createdDate}
            discountLabel={bundle.discountLabel}
            heading={bundle.heading}
            priceDescription={bundle.priceDescription}
            product={bundle.product}
            translations={this.state.translations}
            totalPrice={bundle.totalPrice}
            totalSaving={bundle.totalSavings}
          >
            {this.renderAddBundleToBasketButton(bundle)}
          </Bundle>
        ))}
      </BoxSizing>
    );
  }

  renderAddBundleToBasketButton(bundle) {
    return (
      <AddToBasketButtonWithAjaxCall
        psaVariant={this.props.psaVariant}
        fupid={bundle.id}
        quantity={1}
        mainProductId={bundle.product.id}
        mainProductSegmentId={bundle.product.segmentId}
        addonProductsIds={bundle.addons.map((addon) => addon.id)}
        addonProductsNames={bundle.addons.map((addon) => addon.name)}
        addonProductsSegmentIds={bundle.addons.map((addon) => addon.segmentId)}
        bundlePrice={parseFloat(bundle.totalPrice.match(/\d+(\.\d+)?$/)[0])}
        visitorId={window.adobeVisID ? window.adobeVisID : undefined}
        buttonLabel={this.state.translations.addToBasket}
        full={false}
        beforeAddingToBasket={() => {
          this.props.onAddToBasket({
            bundleProductIds: [bundle.id, ...bundle.addons.map((addon) => addon.id)],
            bundleType: bundle.bundleType
          });
        }}
      />
    );
  }

  render() {
    const { props, state } = this;

    if (state.bundles.length === 0) {
      return <noscript />;
    }

    const bundleList = [...state.bundles];
    const firstBundle = bundleList.shift();

    return (
      <ThemeProvider theme={themes[state.theme]}>
        <DeviceProvider>
          {this.renderBundles([firstBundle])}

          {bundleList.length > 0 && props.bundlesTabsRoots.map((element, i) => (
            <Portal domNode={element} key={i}>
              <span className={'count'}>{bundleList.length}</span>
            </Portal>
          ))}

          {bundleList.length > 0 && props.bundleListRoot && (
            <Portal domNode={props.bundleListRoot}>
              {this.renderBundles(bundleList)}
            </Portal>
          )}

        </DeviceProvider>
      </ThemeProvider>
    );
  }
}

BundlesController.propTypes = {
  bundlesApiPath: PropTypes.string.isRequired,
  bundleListRoot: PropTypes.instanceOf(Element),
  bundlesTabContent: PropTypes.instanceOf(Element),
  bundlesTabsRoots: PropTypes.arrayOf(PropTypes.instanceOf(Element)),
  isProductBundle: PropTypes.bool,
  oldBundlesCount: PropTypes.number,
  userCreatedBundlesCount: PropTypes.number,
  onAddToBasket: PropTypes.func,
  onBundlesReceived: PropTypes.func,
  storeApiUrl: PropTypes.string.isRequired,
  automatedBundlesAllowed: PropTypes.bool,
  tabsHideable: PropTypes.bool.isRequired,
  psaVariant: PropTypes.string.isRequired
};

BundlesController.defaultProps = {
  bundleListRoot: null,
  bundlesTabContent: null,
  bundlesTabsRoots: [],
  isProductBundle: false,
  oldBundlesCount: 0,
  userCreatedBundlesCount: 0,
  onAddToBasket: () => {},
  automatedBundlesAllowed: false,
  onBundlesReceived: () => {}
};

export default function initBundles(psaVariant = PSA_SWITCHED_OFF) {
  const bundleFirstRoot = document.querySelector(SELECTOR_BUNDLES_FIRST);
  const bundleListRoot = document.querySelector(SELECTOR_BUNDLES_LIST);
  const productIdElement =
    document.querySelector(SELECTOR_PRODUCT_ID_STEP_PAGE);
  const bundlesTabsRootsList = document.querySelectorAll(SELECTOR_BUNDLES_TABS);
  const bundlesTabContent = document.querySelector(SELECTOR_BUNDLES_TAB_CONTENT);
  const bundlesBuilder = document.querySelector(SELECTOR_BUNDLES_BUILDER);
  const bundlesOld = document.querySelectorAll(SELECTOR_BUNDLES_OLD);
  const bundlesUserCreated = document.querySelectorAll(SELECTOR_BUNDLES_USER_CREATED);
  const bundlesTabsRoots = [];

  for (let i = 0; i < bundlesTabsRootsList.length; ++i) {
    bundlesTabsRoots.push(bundlesTabsRootsList[i]);
  }

  if (bundleFirstRoot) {
    window.addEventListener('load', () => { // Ensures that window.adobeVisID is available
      const productId = productIdElement.dataset.fupid
        ? productIdElement.dataset.fupid
        : productIdElement.value;
      const visitorId = window.adobeVisID || 0;
      const storeApiUrl = window.App.settings.get('bundles.api') || BUNDLES_API_FALLBACK;
      const bundlesApiPath = BUNDLES_API_PATH
        .replace('[PRODUCT_ID]', productId)
        .replace('[VISITOR_ID]', visitorId);
      const automatedBundlesAllowed = window.App.settings.get('component.sca.active');

      ReactDOM.render(
        React.createElement(
          withDataLayer(BundlesController),
          {
            automatedBundlesAllowed,
            storeApiUrl,
            bundlesApiPath,
            bundleListRoot,
            bundlesTabsRoots,
            bundlesTabContent,
            isProductBundle: productId.startsWith('B'),
            tabsHideable: !bundlesBuilder,
            oldBundlesCount: bundlesOld.length,
            userCreatedBundlesCount: bundlesUserCreated.length,
            psaVariant
          }
        ),
        bundleFirstRoot
      );
    });
  } else {
    dispatchSetProductBundlesList([], bundlesOld.length, bundlesUserCreated.length);
  }
}
